import { useConstructor } from './store/useConstructor';
import { use } from 'react';
import { resolveKnob } from './store/resolveTokens';
import { ConstructorTemplateContext } from './store/ConstructorTemplate';

export interface VariantPickerProps {
  componentName: string;
  /** Knob to write the chosen value into. Defaults to `'style'` — matches
   *  the convention used by `BottomActionStyle.style ∈ {'disc','pill'}`.
   *  Ignored in controlled (UI-only) mode (see `value`/`onChange`). */
  knobName?: string;
  /** Visible label above the radios (e.g. "Shape variant"). */
  label?: string;
  /** Allowed values. First entry is treated as the default when no override
   *  is set and no resolved value exists. */
  variants: readonly string[];
  /**
   * UI-only (controlled) mode. When BOTH `value` and `onChange` are
   * supplied, the picker stops touching the constructor store entirely —
   * it neither reads from `overrides[component][knob]` nor calls
   * `setOverride`. Used by per-variant DS components such as `Button`,
   * where the picker only filters which knobs the editor shows; the CSS
   * variable it would otherwise write is meaningless because every
   * variant of `Button` already has its own dedicated CSS variables.
   */
  value?: string;
  onChange?: (next: string) => void;
}

/**
 * VariantPicker — radio-style switch between enum variants of a component.
 *
 * Two modes:
 *
 *   1. Store-bound (default). The selection is persisted into
 *      `overrides[componentName][knobName]`, so backend round-trip and
 *      reset semantics are uniform with the rest of the editor.
 *      Used when a component's variant maps to a real CSS-var write
 *      (e.g. `BottomActionShell.gradientDirection`).
 *
 *   2. UI-only / controlled. Activated when both `value` and `onChange`
 *      props are supplied. The store is not touched. Used by components
 *      that expose one CSS-var triplet per variant — the picker just
 *      tells `ComponentSection` which subset of knobs to show.
 *
 * Renders nothing when `variants` is empty.
 */
export function VariantPicker({
  componentName,
  knobName = 'style',
  label = 'Variant',
  variants,
  value,
  onChange,
}: VariantPickerProps) {
  const controlled = value !== undefined && onChange !== undefined;
  const { state, setOverride } = useConstructor();
  const template = use(ConstructorTemplateContext);

  if (!variants || variants.length === 0) return null;

  let current: string;
  if (controlled) {
    current = value ?? variants[0]!;
  } else {
    const override = state.value.overrides?.[componentName]?.[knobName];
    const resolved = resolveKnob(state.value, componentName, knobName, template);
    current =
      typeof override === 'string' && override
        ? override
        : typeof resolved === 'string' && resolved
          ? resolved
          : variants[0]!;
  }

  return (
    <div className="mb-3 border-b border-neutral-800 pb-3">
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-neutral-400">
        {label}
      </div>
      <div
        role="radiogroup"
        aria-label={`${componentName} ${knobName} variant`}
        className="flex flex-wrap gap-1.5"
      >
        {variants.map((v) => {
          const active = v === current;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`${knobName}: ${v}`}
              onClick={() => {
                if (controlled) {
                  onChange!(v);
                } else {
                  setOverride(componentName, knobName, v);
                }
              }}
              className={
                'rounded border px-2.5 py-1 font-mono text-[11px] transition-colors ' +
                (active
                  ? 'border-white bg-white text-black'
                  : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:text-white')
              }
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}
